/**
 * SendGiftSheetContent
 *
 * Inner content of the SendGiftSheet (rendered by SheetService).
 * Mirrors Flutter's send_gift_bottom_sheet.dart: 3-column gift grid with
 * select-to-pick / tap-again-to-increment, plus dynamic Send/Recharge CTA.
 *
 * Flutter: lib/presentation/bottom_sheet/send_gift_bottom_sheet.dart
 */
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useGridColumnsCount } from '@/presentation/hooks';
import { CTAButtons } from '@/presentation/components/common/sheet/service/components/CTAButtons';
import { SheetCTAFooter } from '@/presentation/components/common/sheet/service/components/SheetCTAFooter';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { useSendGift, type SendGiftTarget } from '@/presentation/feature/gift/hooks/useSendGift';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { GiftGridItem } from './GiftGridItem';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';

interface SendGiftSheetContentProps {
  /**
   * Where the gift is being sent — `{ kind: SendGiftTargetType.media, media }` for the content
   * flow, `{ kind: SendGiftTargetType.chat, peerUser }` for the chat flow, or
   * `{ kind: SendGiftTargetType.videoCall, callerUser, audienceUser, callMessageId }` for the
   * 1:1 chat video-call flow. Forwarded straight into `useSendGift`, which branches the send path.
   */
  target: SendGiftTarget;
  screenKey?: string;
  /** Media-flow-only pre-selection; chat callers don't supply one. */
  defaultGift?: GiftEntity;
  onClose: () => void;
}

export const SendGiftSheetContent: React.FC<SendGiftSheetContentProps> = ({
  target,
  screenKey,
  defaultGift,
  onClose,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const gridColumnsCount = useGridColumnsCount();

  const {
    gifts,
    isLoadingGifts,
    selectedIndex,
    instances,
    shouldRecharge,
    isSending,
    initialScrollIndex,
    selectGift,
    executeSend,
  } = useSendGift(target, defaultGift);

  const handleSend = useCallback(async () => {
    const success = await executeSend();
    if (success) {
      onClose();
    } else {
      toast.error(l10n.gift.sendGiftError);
    }
  }, [executeSend, onClose, l10n]);

  const handleRechargeWallet = useCallback(() => {
    InfoSheet.show({
      title: l10n.content.rechargeWallet,
      infoText: l10n.content.rechargeNotAvailable,
      screenKey,
    });
  }, [l10n, screenKey]);

  const positiveText = shouldRecharge ? l10n.gift.recharge : l10n.gift.send;
  const positiveDisabled = isSending || (!shouldRecharge && selectedIndex === -1);
  const positiveColor = selectedIndex === -1 && !shouldRecharge
    ? theme.text.secondary
    : theme.exclusive;

  const onPositiveClick = shouldRecharge ? handleRechargeWallet : handleSend;

  // Coarse lifecycle token + selected-gift value for the qa-tester, derived from
  // existing state only.
  const sheetStatus = isLoadingGifts
    ? 'loading'
    : gifts.length === 0
      ? 'empty'
      : 'ready';
  const selectedGiftId = selectedIndex >= 0 ? gifts[selectedIndex]?.id : undefined;

  const renderGiftItem = useCallback(
    (gift: GiftEntity, index: number) => (
      <GiftGridItem
        gift={gift}
        selected={selectedIndex === index}
        instances={selectedIndex === index ? instances : 0}
        onTap={() => selectGift(index)}
      />
    ),
    [selectedIndex, instances, selectGift],
  );

  return (
    <div
      {...qaAttr({ id: 'send-gift-sheet', status: sheetStatus, ...(selectedGiftId ? { value: selectedGiftId } : {}) })}
      className="flex flex-col"
    >
      {/* Gift grid */}
      {isLoadingGifts ? (
        <div
          className="flex items-center justify-center"
          style={{ minHeight: responsive.spacing['3xl'] }}
        />
      ) : gifts.length === 0 ? (
        <div
          className="flex items-center justify-center"
          style={{ minHeight: responsive.avatar.medium * 3 }}
        >
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.sm }}>
            {l10n.gift.noGifts}
          </span>
        </div>
      ) : (
        <VirtualList
          qa={{ id: 'send-gift-list' }}
          items={gifts}
          renderItem={renderGiftItem}
          getItemKey={(gift) => gift.id}
          initialIndex={initialScrollIndex}
          backgroundColor={theme.transparent}
          style={{ marginBottom: responsive.spacing['2xl'] }}
          gridProps={{
            columnsCount: gridColumnsCount,
            crossAxisSpacing: responsive.spacing.lg,
            mainAxisSpacing: responsive.spacing.lg,
            itemAspectRatio: 1,
            padding: responsive.spacing.lg,
          }}
        />
      )}

      {/* CTA */}
      {/* bottomOffset -1: slight overlap to hide the VirtualList's behind it */}
      <SheetCTAFooter bottomOffset={-1}>
        <CTAButtons
          cta={{
            buttons: [
              {
                text: positiveText,
                onClick: onPositiveClick,
                disabled: positiveDisabled,
                loading: isSending,
                closeOnClick: false,
                textColor: positiveColor,
                qaId: 'send-gift-confirm',
              },
              {
                text: l10n.common.cancel,
                onClick: onClose,
              },
            ],
          }}
          onClose={() => { }}
        />
      </SheetCTAFooter>
    </div>
  );
};
